import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Info } from "lucide-react";
import type { ProfileOption } from "@amber/profiles/contracts";
import {
  AppLink,
  Button,
  Drawer,
  FailedCallout,
  FieldHint,
  FieldLabel,
  ReasonField,
  TextField,
  TextInput,
  cx,
  isApiError,
  useReason,
  useToast,
  useCommandMutation,
  useDebounced,
} from "@amber/ui";
import {
  collectionCommands,
  profileOptionsQuery,
  urlCheckQuery,
  AFTER_COLLECTION_CHANGE,
  ON_COLLECTION_CONFLICT,
} from "../queries";

function todayLocal(): string {
  const now = new Date();
  return `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function checkTone(outcome: string): string {
  if (outcome === "new") return "text-done";
  return outcome === "invalid" ? "text-attention" : "text-waiting";
}

function DuplicateCheck({ url }: { url: string }) {
  const check = useQuery({ ...urlCheckQuery(url), enabled: url.length > 0 });
  if (url.length === 0 || !check.data) return null;
  const c = check.data;
  const tone = checkTone(c.outcome);
  return (
    <p role="status" className={cx("m-0 mt-1.5 flex items-start gap-1.5 text-md", tone)}>
      {c.outcome === "new" ? (
        <CheckCircle2 size={13} aria-hidden="true" className="mt-px flex-none" />
      ) : (
        <Info size={13} aria-hidden="true" className="mt-px flex-none" />
      )}
      <span>
        {c.message}
        {c.item ? (
          <>
            {" · "}
            <AppLink href={`/backlog/${c.item.id}`} className="font-strong text-link">
              Show it
            </AppLink>
          </>
        ) : null}
      </span>
    </p>
  );
}

function lifecycleLabel(state: ProfileOption["lifecycle_state"]): string {
  if (state === "draft") return "Draft";
  return state === "incidental" ? "Not tracked" : "Paused";
}

function PeopleField({
  options,
  people,
  setPeople,
  find,
  setFind,
}: {
  options: readonly ProfileOption[];
  people: string[];
  setPeople: (next: string[]) => void;
  find: string;
  setFind: (next: string) => void;
}) {
  return (
    <fieldset className="m-0 mt-3 min-w-0 border-0 p-0">
      <legend className="contents">
        <FieldLabel optional="optional">People</FieldLabel>
      </legend>
      <label className="sr-only" htmlFor="add-item-find">
        Find a person
      </label>
      <TextInput
        id="add-item-find"
        type="search"
        placeholder="Find a person"
        value={find}
        onChange={(e) => {
          setFind(e.target.value);
        }}
      />
      <div className="mt-1 max-h-[148px] overflow-auto rounded-lg border border-divider p-1">
        {options.length === 0 ? <p className="m-0 px-2 py-1 text-md text-muted">No matching person</p> : null}
        {options.map((p) => (
          <label
            key={p.id}
            className="flex h-[27px] cursor-pointer items-center gap-2 rounded-md px-2 hover:bg-surface-2"
          >
            <input
              type="checkbox"
              checked={people.includes(p.id)}
              onChange={() => {
                setPeople(people.includes(p.id) ? people.filter((x) => x !== p.id) : [...people, p.id]);
              }}
              className="accent-[var(--color-link)]"
            />
            <span className="min-w-0 flex-1 truncate">{p.name}</span>
            {p.lifecycle_state === "active" ? null : (
              <span className="text-xs text-muted">{lifecycleLabel(p.lifecycle_state)}</span>
            )}
          </label>
        ))}
      </div>
      <FieldHint>Who communicates in it, if you know. Relevance checks still run.</FieldHint>
    </fieldset>
  );
}

export function AddItemPanel({
  initialUrl,
  onClose,
  onAdded,
}: {
  initialUrl?: string | undefined;
  onClose: () => void;
  onAdded: (id: string) => void;
}) {
  const toast = useToast();
  const [url, setUrl] = useState(initialUrl ?? "");
  const [people, setPeople] = useState<string[]>([]);
  const [find, setFind] = useState("");
  const [date, setDate] = useState("");
  const checkedUrl = useDebounced(url.trim(), 300);
  const profiles = useQuery(profileOptionsQuery);
  const check = useQuery({ ...urlCheckQuery(checkedUrl), enabled: checkedUrl.length > 0 });
  const mutation = useCommandMutation({
    run: (csrf, reason: string) =>
      collectionCommands.add({ url: url.trim(), people, ...(date ? { date } : {}), reason }, csrf),
    invalidate: AFTER_COLLECTION_CHANGE,
    invalidateOnConflict: ON_COLLECTION_CONFLICT,
    onSuccess: (result) => {
      toast.show("Added as New");
      onAdded(result.id);
    },
  });
  const server = isApiError(mutation.error) ? mutation.error.fieldErrors() : {};
  const reason = useReason(server["reason"]);
  const submitted = reason.submitted;
  const urlError = server["url"] ?? (submitted && url.trim().length === 0 ? "Enter the link to the item" : null);
  const today = todayLocal();
  const dateError = server["date"] ?? (submitted && date > today ? "The date can't be in the future" : null);
  const blocked = check.data !== undefined && check.data.outcome !== "new" && checkedUrl === url.trim();
  const options = useMemo(() => {
    const all = profiles.data?.profiles ?? [];
    const term = find.trim().toLowerCase();
    return all.filter(
      (p) =>
        p.lifecycle_state !== "archived" &&
        (term === "" || p.name.toLowerCase().includes(term) || people.includes(p.id)),
    );
  }, [profiles.data, find, people]);

  const submit = () => {
    const ok = reason.submit();
    if (url.trim().length === 0 || !ok || date > today || blocked) return;
    mutation.mutate(reason.trimmed);
  };

  return (
    <Drawer
      title="Add item"
      subtitle="Add a missing appearance or original by its link"
      onClose={onClose}
      footer={
        <span className="flex items-center justify-end gap-2">
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            type="submit"
            form="add-item-form"
            aria-busy={mutation.isPending || undefined}
          >
            {mutation.isError ? "Try again" : "Add item"}
          </Button>
        </span>
      }
    >
      <form
        id="add-item-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="text-ui [&>div:first-child]:mt-0"
      >
        <TextField
          label="Link"
          required
          type="url"
          inputMode="url"
          autoComplete="off"
          placeholder="https://"
          value={url}
          error={urlError}
          onChange={(e) => {
            setUrl(e.target.value);
          }}
        />
        <DuplicateCheck url={checkedUrl} />
        <PeopleField options={options} people={people} setPeople={setPeople} find={find} setFind={setFind} />
        <TextField
          label="Date"
          optional="optional"
          type="date"
          max={today}
          value={date}
          error={dateError}
          hint="When it happened or was published; not in the future"
          onChange={(e) => {
            setDate(e.target.value);
          }}
        />
        <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
        <p className="m-0 mt-3 text-md text-muted">
          The item goes through the same duplicate, relevance and processing checks as discovered items. Adding it
          publishes nothing and doesn&apos;t start monitoring its source.
        </p>
        <FailedCallout error={mutation.error} fieldsShown />
      </form>
    </Drawer>
  );
}
